/** Global Ctrl/Cmd+K command palette — navigation across all modules, permission-aware. */
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "@/components/ui/command";
import { ArrowBendUpLeft } from "@phosphor-icons/react";

const CATALOG = [
  { menu: null, group: "Workflow", items: [
    ["/my-tasks", "My Tasks — Workflow Inbox"],
  ]},
  { menu: "treasury", group: "Dashboard & Treasury", items: [
    ["/dashboard", "Dashboard"], ["/fixed-deposits", "Fixed Deposits"], ["/loans", "Loans"],
    ["/bank-guarantees", "Bank Guarantees"], ["/letters-of-credit", "Letters of Credit"],
    ["/transactions", "Transactions"], ["/fx", "FX & MTM"], ["/bank-accounts", "Bank Accounts"],
    ["/reconciliation", "Reconciliation"], ["/approvals", "Approvals"],
  ]},
  { menu: "p2p_compliance", group: "Procure to Pay", items: [
    ["/p2p-compliance", "Compliance Agent"],
  ]},
  { menu: "epm", group: "EPM", items: [
    ["/epm/consolidation", "Entity Consolidation"],
  ]},
  { menu: "epbcs", group: "Planning & Budgeting", items: [
    ["/planning/setup/masters", "Dimension Masters"], ["/planning/setup/cycle", "Budget Cycle"],
    ["/planning/setup/matrix", "Role Matrix"], ["/planning/setup/users", "User Access"],
    ["/planning/setup/import", "Data Import"],
    ["/planning/revenue/other", "Revenue — Other Revenue"],
    ["/planning/revenue/driver", "Revenue — Driver Based"],
    ["/planning/revenue/contract", "Revenue — Contract Based"],
    ["/planning/revenue/product", "Revenue — Product Based"],
    ["/planning/revenue/hosp1", "Revenue — Hospitality (CY Forecast)"],
    ["/planning/revenue/hosp2", "Revenue — Hospitality (Budget Year)"],
    ["/planning/opex/contract", "OpEx — Contract Based"],
    ["/planning/opex/driver", "OpEx — Driver Based"],
    ["/planning/opex/other", "OpEx — Other Expenses"],
    ["/planning/opex/hosp1", "OpEx — Hospitality (CY Forecast)"],
    ["/planning/opex/hosp2", "OpEx — Hospitality (Budget Year)"],
    ["/planning/opex/material", "OpEx — Material Cost"],
    ["/planning/opex/bom", "OpEx — Material BOM Master"],
    ["/planning/opex/fuel", "OpEx — Fuel Cost"],
    ["/planning/opex/fleet", "OpEx — Transportation Fleet"],
    ["/planning/capex/wbs_new", "CapEx — New Projects"],
    ["/planning/capex/wbs_existing", "CapEx — Existing Projects"],
    ["/planning/capex/planning", "CapEx — Capital Planning"],
    ["/planning/capex/assets_new", "CapEx — New Asset Procurement"],
    ["/planning/capex/assets_existing", "CapEx — Existing Assets"],
    ["/planning/capex/combos", "CapEx — Valid Combinations"],
    ["/planning/capex/review", "CapEx — Review"],
    ["/planning/workforce/increment", "Workforce — Increment Assumptions"],
    ["/planning/workforce/attrition", "Workforce — Attrition / Notice Period"],
    ["/planning/workforce/existing", "Workforce — Existing Employees"],
    ["/planning/workforce/hires", "Workforce — New Hires"],
    ["/planning/workforce/scales", "Workforce — Pay Scales"],
    ["/planning/workforce/review", "Workforce — Review"],
    ["/planning/financials/income", "Financials — Income Statement"],
    ["/planning/financials/balance", "Financials — Balance Sheet"],
    ["/planning/financials/approvals", "Financials — Approval Workflow"],
    ["/planning/financials/workflow", "Financials — Workflow Setup"],
    ["/planning/dashboards", "Planning Dashboards"],
  ]},
  { menu: "commodity", group: "Commodity Asset Mgmt", items: [
    ["/commodity/dashboard", "Commodity Dashboard"],
    ["/commodity/deals", "Trade Capture"],
    ["/commodity/movements", "Scheduling & Logistics"],
    ["/commodity/inventory", "Commodity Inventory"],
    ["/commodity/reconciliation", "Back-Office Reconciliation"],
    ["/commodity/masters", "Commodity Setup & Masters"],
  ]},
  { menu: "reporting_agent", group: "Reporting Agent", items: [
    ["/reporting/ai", "Ask AI"], ["/reporting/catalog", "Report Catalog"],
    ["/reporting/queries", "Report Queries"], ["/reporting/connection", "Fusion Connection"],
  ]},
  { menu: "finance_efficiency", group: "Finance Efficiency", items: [
    ["/automations", "Automations"],
  ]},
  { menu: "telecom", group: "Telecom Solution", items: [
    ["/telecom/finance-bss", "Finance × BSS"], ["/telecom/revenue-assurance", "Revenue Assurance"],
    ["/telecom/spectrum", "Spectrum & Licences"], ["/telecom/slicing", "5G Slicing Billing"],
    ["/alm/dashboard", "ALM Dashboard"], ["/alm/map", "Live Asset Map"],
    ["/alm/products", "Product Master"], ["/alm/sites", "Site Master"],
    ["/alm/locations", "Location Master"], ["/alm/procurement-plans", "Plan to Procure"],
    ["/alm/receipts", "Receiving Process"], ["/alm/assets", "Asset Tracking"],
    ["/alm/movements", "Movement of Items"], ["/alm/capitalizations", "Asset Capitalization"],
    ["/alm/maintenance", "Asset Maintenance"], ["/alm/retirements", "Asset Retirement"],
  ]},
  { menu: "admin", group: "Admin Console", adminOnly: true, items: [
    ["/admin/agent-setup", "Agent Setup"], ["/admin/users", "Users & Access"],
    ["/admin/llm-settings", "LLM Settings"],
  ]},
  { menu: "vakuai_db", group: "Admin Console", adminOnly: true, items: [
    ["/admin/database", "Vakuai Database"],
  ]},
];

export default function CommandPalette({ open, setOpen, canSee, isProductAdmin }) {
  const nav = useNavigate();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setOpen]);

  const groups = CATALOG.filter((g) => (!g.menu || canSee(g.menu)) && (!g.adminOnly || isProductAdmin));

  const go = (to) => {
    setOpen(false);
    nav(to);
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Jump to any page…" data-testid="command-palette-input" />
      <CommandList className="max-h-[420px]" data-testid="command-palette-list">
        <CommandEmpty>No pages found.</CommandEmpty>
        {groups.map((g) => (
          <CommandGroup key={`${g.menu}-${g.items[0][0]}`} heading={g.group}>
            {g.items.map(([to, label]) => (
              <CommandItem key={to} value={`${g.group} ${label} ${to}`}
                onSelect={() => go(to)} data-testid={`palette-item-${to.replace(/\//g, "-").slice(1)}`}
                className="cursor-pointer">
                <ArrowBendUpLeft className="rotate-180 text-slate-400" />
                <span>{label}</span>
                <span className="ml-auto text-[10px] font-mono text-slate-400">{to}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
      </CommandList>
    </CommandDialog>
  );
}
